import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ConstraintPriorities, ConstraintsItemType } from '../components/Constraints/types';

/**
 * Flat teacher constraints blob written on the teacher↔version edge. Kept flat (not nested) because
 * the Timefold export reads these exact keys — do not restructure.
 */
export type TeacherConstraints = {
  maxHoursPerDay?: number;
  maxHoursPriority?: ConstraintPriorities;
  minHoursPerDay?: number;
  maxDaysPerWeek?: number;
  maxDaysPriority?: ConstraintPriorities;
  minDaysPerWeek?: number;
  maxOnUnknownPosition?: number;
  maxOnUnknownPriority?: ConstraintPriorities;
  maxConsecutive?: number;
  maxConsecutivePriority?: ConstraintPriorities;
  maxFreePeriod?: number;
  maxFreePeriodPriority?: ConstraintPriorities;
  maxBuildingChangesPerDay?: number;
  maxBuildingChangesPerDayPriority?: ConstraintPriorities;
  maxBuildingChangesPerWeek?: number;
  maxBuildingChangesPerWeekPriority?: ConstraintPriorities;
};

/** Which set of number options a field offers (computed in the form from the shared number list). */
export type ConstraintFieldRange = 'posPerDay' | 'perWeek' | 'fromZero';

export type TeacherFieldDescriptor = {
  /** Numeric field on the blob (also the key into {@link teacherConstraintLabels}). */
  field: keyof TeacherConstraints;
  /** Sibling priority field, if the constraint carries a priority. */
  priorityField?: keyof TeacherConstraints;
  range: ConstraintFieldRange;
};

/** Single source of truth for the teacher conditions: drives the list, the form and field deletion. */
export const teacherConstraintFields: TeacherFieldDescriptor[] = [
  { field: 'maxHoursPerDay', priorityField: 'maxHoursPriority', range: 'posPerDay' },
  { field: 'minHoursPerDay', range: 'posPerDay' },
  { field: 'maxDaysPerWeek', priorityField: 'maxDaysPriority', range: 'perWeek' },
  { field: 'minDaysPerWeek', range: 'perWeek' },
  { field: 'maxConsecutive', priorityField: 'maxConsecutivePriority', range: 'posPerDay' },
  { field: 'maxOnUnknownPosition', priorityField: 'maxOnUnknownPriority', range: 'fromZero' },
  { field: 'maxFreePeriod', priorityField: 'maxFreePeriodPriority', range: 'fromZero' },
  { field: 'maxBuildingChangesPerDay', priorityField: 'maxBuildingChangesPerDayPriority', range: 'fromZero' },
  { field: 'maxBuildingChangesPerWeek', priorityField: 'maxBuildingChangesPerWeekPriority', range: 'fromZero' },
];

/**
 * Display labels for the teacher conditions, keyed by field. Literal `t('...')` calls (not
 * `t(labelKey)`) so the i18next-parser extracts them — no dynamic keys, no manifest entry.
 * Shared by the summary list ({@link useTeacherConstraints}) and the form.
 */
export const teacherConstraintLabels = (t: TFunction): Record<string, string> => ({
  maxHoursPerDay: t('conditions.teacher.maxHoursPerDay'),
  minHoursPerDay: t('conditions.teacher.minHoursPerDay'),
  maxDaysPerWeek: t('conditions.teacher.maxDaysPerWeek'),
  minDaysPerWeek: t('conditions.teacher.minDaysPerWeek'),
  maxConsecutive: t('conditions.teacher.maxConsecutive'),
  maxOnUnknownPosition: t('conditions.teacher.maxOnUnknownPosition'),
  maxFreePeriod: t('conditions.teacher.maxFreePeriod'),
  maxBuildingChangesPerDay: t('conditions.teacher.maxBuildingChangesPerDay'),
  maxBuildingChangesPerWeek: t('conditions.teacher.maxBuildingChangesPerWeek'),
});

/** Builds the displayed constraint list from a teacher blob (shared {@link ConstraintsItemType}). */
export const useTeacherConstraints = (constraints: TeacherConstraints): ConstraintsItemType[] => {
  const { t } = useTranslation();
  return useMemo(() => {
    const labels = teacherConstraintLabels(t);
    const items: ConstraintsItemType[] = [];
    for (const descriptor of teacherConstraintFields) {
      const value = constraints[descriptor.field] as number | undefined;
      if (value == null) continue;
      const priority = descriptor.priorityField
        ? ((constraints[descriptor.priorityField] as ConstraintPriorities | undefined) ?? null)
        : null;
      items.push({ type: descriptor.field, priority, text: `${labels[descriptor.field]}: ${value}` });
    }
    return items;
  }, [t, constraints]);
};
